import styled from "@emotion/styled";
import { ModalLayout } from "../../components/common/ModalLayout";
import theme from "../../theme";
import { Box, Pagination } from "@mui/material";
import { useEffect, useState } from "react";
import useGetDetectEventDevice from "../../hooks/useGetDetectEventDevice";
import Spinner from "../../components/spinner";
import { deviceImageArray } from "../../utils/deviceImageArray";
import { CustomTooltip } from "../../components/common/CustomTooltip";
import { useWindowSize } from "@reactuses/core";
import { formatDate, textSubstring } from "../../utils/util";
import { useI18n } from "../../locales";
import { getMobilityTypeText } from "../../utils/langUtils";

const { sm, md, lg } = theme.screen;

interface Props {
  open: boolean;
  onPressNo: () => void;
  eventCode: number;
  startDate: string;
  endDate: string;
  groupId: string;
  mobility_code: number;
}

/**
 * 통계
 *
 * 종류별 감지내역 기기 목록 모달
 */
export const MobilityListModal = ({
  open,
  onPressNo,
  eventCode,
  startDate,
  endDate,
  groupId,
  mobility_code,
}: Props) => {
  const { t } = useI18n();
  const [page, setPage] = useState(0);
  const { width } = useWindowSize();
  const [mobSubstringLength, setMobSubstringLength] = useState(19);

  useEffect(() => {
    if (width > lg) {
      setMobSubstringLength(31);
      return;
    }
    if (width > md && width <= lg) {
      setMobSubstringLength(27);
      return;
    }
    if (width <= md) {
      setMobSubstringLength(17);
      return;
    }
  }, [width]);

  const { data, isLoading } = useGetDetectEventDevice({
    startDate,
    endDate,
    event_code: eventCode,
    ...(groupId && groupId !== "all" && { groupId }),
    limit: 10,
    mobility_code,
    page: page,
  });

  const handlePageChange = (
    // 페이지네이션
    _: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value - 1);
  };

  const getTitle = () => {
    if (eventCode === -1) {
      return t.statistics.operatedMachinery;
    }
    if (eventCode === 0) {
      return t.statistics.totalDetections;
    }
    if (eventCode === 2) {
      return t.statistics.rolloverDetections;
    }
    if (eventCode === 3) {
      return t.statistics.dangerousDrivingDetections;
    }
    if (eventCode === 5) {
      return t.statistics.ignitionNeglectDetections;
    }
    if (eventCode === 6) {
      return t.statistics.outOfAreaDetections;
    }
    if (eventCode === 8) {
      return t.statistics.batteryAnomalyDetections;
    }
    if (eventCode === 9) {
      return t.statistics.dangerousAreaEntryDetections;
    }
    return "";
  };

  return (
    <ModalLayout
      open={open}
      onClose={onPressNo}
      width="770px"
      title={t.control.deviceList}
    >
      <MobilityWrapperStyledDiv>
        <ListTitleStyledH2>{`${getTitle()} ${
          data?.total_items ? `(${data.total_items}${t.statistics.units})` : ""
        }`}</ListTitleStyledH2>
        <MobilityListStyledDiv>
          {isLoading && <Spinner />}
          {data?.list.length === 0 && (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {t.control.noDeviceFound}
            </Box>
          )}
          {data?.list.map((el) => (
            <ListItemStyledDiv
              hoverColor={theme.color.neutral_100}
              // style={{ cursor: "pointer" }}
              key={el.seq}

              //  active={selectedMobility?.device_pid === el.device_pid}
            >
              <ImageWrapperStyledDiv
                bgColor={
                  el?.status === "운행중" || el?.status === t.control.operationRunning
                    ? theme.color.green_100
                    : theme.color.neutral_100
                }
                bdColor={
                  el?.status === "운행중" || el?.status === t.control.operationRunning
                    ? "rgba(92, 200, 109, 0.5)"
                    : "rgba(26, 39, 35, 0.5)"
                }
              >
                {el?.mobility_image === null ? (
                  <MobilityStyledImg
                    src={`${process.env.REACT_APP_BASE_URL}/img/${
                      deviceImageArray.find(
                        (list) => list.device === el.mobility_type
                      )?.listFileName
                    }`}
                  />
                ) : (
                  <MobilityStyledImg
                    src={`${process.env.REACT_APP_BASE_URL}/${el?.mobility_image}`}
                    isEtc
                  />
                )}

                {/* <MobilityStyledImg
                  src={`${process.env.REACT_APP_BASE_URL}/img/${
                    deviceImageArray.find(
                      (list) => list.device === el.mobility_type
                    )?.listFileName
                  }`}
                /> */}
              </ImageWrapperStyledDiv>
              <InfoWrapperStyledDiv>
                {/* 기기명[기기종류] */}
                <CustomTooltip
                  title={el.mobility_name}
                  active={el.mobility_name?.length > mobSubstringLength}
                >
                  <MobilityNameStyledSpan>
                    {(() => {
                      const typeLabel = getMobilityTypeText(el.mobility_type, t);
                      const typeLength = typeLabel.length;
                      if (el.mobility_name == null) return "";

                      if (el.mobility_name.length > mobSubstringLength) {
                        return (
                          el.mobility_name.substring(
                            0,
                            Math.max(mobSubstringLength - typeLength, 0)
                          ) + "..."
                        );
                      }

                      return el.mobility_name;
                    })()}
                    [{getMobilityTypeText(el.mobility_type, t)}]
                  </MobilityNameStyledSpan>
                </CustomTooltip>

                {/* 사용자명 */}
                <CustomTooltip
                  title={el.name}
                  active={el.name?.length > (width > md ? 30 : 16)}
                >
                  <UserNameStyledSpan>
                    {t.statistics.user} :{" "}
                    {textSubstring(el.name, 0, width > md ? 30 : 16)}
                  </UserNameStyledSpan>
                </CustomTooltip>

                <LastRunTimeStyledSpan>
                  {t.statistics.lastOperation} :
                  {!el.gps_date && !el.gps_date
                    ? t.statistics.noDrivingRecord
                : formatDate(
                        el.gps_date ?? el.gps_date,
                        "yyyy-MM-dd HH:mm"
                      )}
                </LastRunTimeStyledSpan>
              </InfoWrapperStyledDiv>
            </ListItemStyledDiv>
          ))}
        </MobilityListStyledDiv>
        <Pagination
          page={page + 1}
          count={data?.total_page}
          onChange={handlePageChange}
          // siblingCount={width > md ? 4 : width > sm ? 3 : 2}
          size="small"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            justifySelf: "end",
          }}
        />
      </MobilityWrapperStyledDiv>
    </ModalLayout>
  );
};

const MobilityWrapperStyledDiv = styled.div`
  background: ${theme.color.neutral_50};
  border: 3px solid ${theme.color.neutral_100};
  border-radius: 20px;
  padding-top: 20px;
  height: 435px;
  display: flex;
  flex-direction: column;
  position: relative;
  width: calc(100vw - 64px - 60px);

  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    min-width: 651px;
    max-width: 651px;
  }
  @media (min-width: ${lg + 1}px) {
    /* min-width: 534px;
    max-width: 534px; */
    min-width: 651px;
    max-width: 651px;
  }

  @media print {
    flex-grow: 1;
  }
`;
const ListTitleStyledH2 = styled.h2`
  font-weight: 700;
  font-size: 15px;
  line-height: 18px;
  color: ${theme.color.neutral_600};
  padding-left: 25px;
  border-bottom: 3px solid ${theme.color.neutral_100};
  padding-bottom: 20px;
`;

const MobilityListStyledDiv = styled.div`
  border: 1px solid ${theme.color.neutral_100};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const ListItemStyledDiv = styled.div<{
  active?: boolean;
  hoverColor?: string;
}>`
  display: flex;
  gap: 20px;
  padding: 22px 20px;
  border-bottom: 1px solid ${theme.color.neutral_200};
  background-color: ${({ active }) =>
    active ? `${theme.color.neutral_100}` : `${theme.color.white}`};
  /* cursor: pointer; */
  /* &:hover {
    background-color: ${({ hoverColor }) => hoverColor};
  } */
  /* 선택된 항목이 제일 앞으로 */
  /* order: ${({ active }) => active && "-1"}; */
  position: relative;

  @media (max-width: ${sm}px) {
    gap: 10px;
    padding: 15px 10px;
  }
`;
const ImageWrapperStyledDiv = styled.div<{
  bgColor: string;
  bdColor: string;
}>`
  background-color: ${({ bgColor }) => bgColor};
  width: 65px;
  height: 61px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: ${({ bdColor }) => `3px solid ${bdColor}`};
  flex-shrink: 0;

  @media (max-width: ${sm}px) {
    width: 55px;
    height: 55px;
    align-self: center;
  }
`;

const MobilityStyledImg = styled.img<{ isEtc?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: ${({ isEtc }) => (isEtc ? `fill` : `contain`)};
`;

const InfoWrapperStyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MobilityNameStyledSpan = styled.span`
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  color: ${theme.color.neutral_700};

  @media (max-width: ${sm}px) {
    font-size: 14px;
  }
  @media (min-width: ${sm + 1}px) and (max-width: ${md}px) {
    font-size: 15px;
  }
`;

export const UserNameStyledSpan = styled.span`
  font-weight: 700;
  color: ${theme.color.neutral_700};
  font-size: 14px;
  line-height: 16px;
  @media (max-width: ${sm}px) {
    font-size: 12px;
  }
  @media (min-width: ${sm + 1}px) and (max-width: ${md}px) {
    font-size: 13px;
  }
`;

export const LastRunTimeStyledSpan = styled.span`
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  color: ${theme.color.neutral_400};
`;
